import {
  Core_Sex,
  Patientportal_PortalUserInviteStatus,
  useListPortalUserInvitesQuery,
  useMarkPortalUserInviteHiddenMutation,
} from '@/generated/graphql';
import { ExportOutlined } from '@ant-design/icons';
import { Col, notification, Row, Space, Typography } from 'antd';
import { NextPage } from 'next';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { InfoCards } from '../../components/home/info-cards';
import { InfoSection } from '../../components/home/info-section';
import { PortalUserInviteModal } from '../../components/home/portal-user-invite-modal';
import { QuestionAndAnswer } from '../../components/home/question-and-answer';
import { UnverifiedAccountAlert } from '../../components/home/unverified-account-alert';
import { UpcomingAppointments } from '../../components/home/upcoming-appointments';
import { HeaderProps, PageWrapper } from '../../components/page-wrapper';
import { SmartSearch } from '../../components/smart-search';
import { buildFullName, getSalutation } from '../../helpers/formatters';
import { useCurrentUser } from '../../helpers/utils';
import { Tenant, TENANT } from '../../tenant/tenant';
import { BACKGROUND_IMAGE } from '../../tenant/tenant-logos';
import { TENANT_WEBSITE } from '../../tenant/tenant-website';
import { ScreenSizeContext } from '../_app';
import styles from './home.module.scss';

const WELCOME_TITLE_IDS: Record<Tenant, { default: string; loggedIn: string }> = {
  [Tenant.sana]: {
    default: 'home.welcome-title.sana',
    loggedIn: 'home.welcome-title.sana.logged-in',
  },
  [Tenant.avelios]: {
    default: 'home.welcome-title.avelios',
    loggedIn: 'home.welcome-title.avelios.logged-in',
  },
};

const Home: NextPage = () => {
  const { formatMessage } = useIntl();
  const { currentUser } = useCurrentUser();

  const welcomeTitleId = currentUser
    ? WELCOME_TITLE_IDS[TENANT].loggedIn
    : WELCOME_TITLE_IDS[TENANT].default;

  const userSex = currentUser?.sex;

  const salutationPresent = userSex && [Core_Sex.SexM, Core_Sex.SexW].includes(userSex);

  const fullName = currentUser
    ? buildFullName(
        currentUser.name,
        salutationPresent ? formatMessage({ id: getSalutation(userSex) }) : undefined,
      )
    : undefined;

  const welcomeTitleLines = formatMessage({ id: welcomeTitleId }, { name: fullName, br: '\n' })
    .split('\n')
    .filter((line) => line.trim().length > 0);

  const [mobile, setMobile] = useState<boolean>(false);

  const { isMobile } = useContext(ScreenSizeContext);

  useEffect(() => {
    setMobile(isMobile);
  }, [isMobile]);

  const { data: portalUserInvites, refetch: refetchPortalUserInvites } =
    useListPortalUserInvitesQuery({
      skip: !currentUser,
      fetchPolicy: 'network-only',
    });

  useEffect(() => {
    if (currentUser?.id) {
      refetchPortalUserInvites();
    }
  }, [currentUser?.id, refetchPortalUserInvites]);

  const activeInvite = useMemo(
    () =>
      portalUserInvites?.listPortalUserInvites?.invites?.find(
        (invite) =>
          invite?.status === Patientportal_PortalUserInviteStatus.PortalUserInviteStatusActive,
      ),
    [portalUserInvites],
  );

  const cancelledOrInvalidInvite = useMemo(
    () =>
      portalUserInvites?.listPortalUserInvites?.invites?.find(
        (invite) =>
          !invite?.hidden &&
          (invite?.status ===
            Patientportal_PortalUserInviteStatus.PortalUserInviteStatusCancelled ||
            invite?.status === Patientportal_PortalUserInviteStatus.PortalUserInviteStatusInvalid),
      ),
    [portalUserInvites],
  );

  const [markInviteHidden] = useMarkPortalUserInviteHiddenMutation();

  useEffect(() => {
    if (!cancelledOrInvalidInvite?.id) {
      return;
    }
    const inviteId = cancelledOrInvalidInvite.id;
    notification.warning({
      key: inviteId,
      message: formatMessage({ id: 'home.portal-user-invite.cancelled-or-invalid' }),
      duration: 0,
      onClose: () => markInviteHidden({ variables: { inviteId } }),
    });
  }, [cancelledOrInvalidInvite?.id, markInviteHidden]);

  return (
    <PageWrapper backgroundImage={BACKGROUND_IMAGE} headerProps={headerProps}>
      <UnverifiedAccountAlert />
      {activeInvite && <PortalUserInviteModal invite={activeInvite} />}
      <Space style={{ width: '100%' }} direction={'vertical'} size={mobile ? 20 : 64}>
        {!mobile && (
          <Row>
            <Col span={24}>
              <div
                className={
                  TENANT === Tenant.sana ? `${styles.title} ${styles.titleSana}` : styles.title
                }
              >
                {welcomeTitleLines.map((line) => (
                  <div key={line}>{line}</div>
                ))}
              </div>
              <span
                className={
                  TENANT === Tenant.sana
                    ? `${styles.subtitle} ${styles.subtitleSana}`
                    : styles.subtitle
                }
              >
                {formatMessage({ id: 'home.welcome-subtitle' })}
              </span>
              <div className={styles.smartSearchWrapper}>
                <Space style={{ width: '100%' }} direction={'vertical'} size={12}>
                  <SmartSearch />
                  {TENANT === Tenant.sana && (
                    <Typography.Link href={TENANT_WEBSITE} target={'_blank'} rel={'noreferrer'}>
                      {formatMessage({ id: 'home.sana-link' })} <ExportOutlined />
                    </Typography.Link>
                  )}
                </Space>
              </div>
            </Col>
          </Row>
        )}
        <Row>
          <Col span={24}>
            <InfoCards />
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <UpcomingAppointments />
          </Col>
        </Row>
        <InfoSection />
        {/*<Row>*/}
        {/*  <Col span={24}>*/}
        {/*    <App />*/}
        {/*  </Col>*/}
        {/*</Row>*/}

        <Row>
          <Col span={24}>
            <QuestionAndAnswer />
          </Col>
        </Row>

        {/*<Row>
          <Col span={24}>
            <TrustedPartners />
          </Col>
        </Row>*/}
      </Space>
    </PageWrapper>
  );
};

const headerProps: HeaderProps = {
  title: 'Homepage',
  keywords: ['avelios medical', 'krankenhaus'],
};

export default Home;
